import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, LogOut, Settings, Crown, Lock, Bell } from 'lucide-react';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useModal } from '../../../shared/contexts/ModalContext';
import { usePlan } from '../../../shared/contexts/PlanContext';
import '../../../styles/teachering/layout/mainHeader.css';
import LoginModal from '../components/LoginModal';
import RegisterModal from '../components/RegisterModal';
import NotificationDropdown from '../components/NotificationDropdown';
// import MarketingConsentModal from '../components/MarketingConsentModal';
import { getUserFromFirestore, getUnreadNotifications, markNotificationAsRead } from '../../../shared/api/firebase';

const TeacheringHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, isAuthenticated, logout } = useAuth();
  const {
    showLoginModal,
    openLoginModal,
    closeLoginModal,
    showRegisterModal,
    openRegisterModal,
    closeRegisterModal,
  } = useModal();
  const { isPro, loading: planLoading } = usePlan();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState('home');
  const [showMarketingModal, setShowMarketingModal] = useState(false);
  const [checkedMarketing, setCheckedMarketing] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminLoaded, setIsAdminLoaded] = useState(false);
  const [showAuthRequiredModal, setShowAuthRequiredModal] = useState(false);

  // 알림 데이터
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [shouldShake, setShouldShake] = useState(false);

  // URL 경로 변경 감지하여 currentPage 상태 업데이트
  useEffect(() => {
    const path = location.pathname;
    
    if (path === '/') {
      setCurrentPage('home');
    } else if (path.includes('contents')) {
      setCurrentPage('contents');
    } else if (path.includes('pricing')) {
      setCurrentPage('pricing');
    } else if (path.includes('class-management')) {
      setCurrentPage('class-management');
    } else if (path.includes('help-center')) {
      setCurrentPage('help-center');
    } else if (path.includes('school-record')) {
      setCurrentPage('school-record');
    } else {
      // 다른 페이지인 경우 활성화 상태 초기화
      setCurrentPage('');
    }
  }, [location.pathname]);

  // 사용자 프로필 정보 생성
  const userProfile = currentUser ? {
    name: currentUser.displayName || currentUser.email?.split('@')[0] || null,
    email: currentUser.email || '',
    avatarUrl: currentUser.photoURL || null
  } : null;

  const isLoggedIn = isAuthenticated;
  
  // 로그인 성공 시 사용자 정보를 먼저 로드한 후 리다이렉트
  useEffect(() => {
    let cancelled = false;
    const handlePostLoginRedirect = async () => {
      if (currentUser) {
        const redirect = localStorage.getItem('postLoginRedirect');
        if (redirect) {
          // 먼저 사용자 정보를 로드
          try {
            const res = await getUserFromFirestore(currentUser.uid);
            if (!cancelled) {
              const data = res && res.data ? res.data : null;
              const adminStatus = !!(data && data.isAdmin === true);
              setIsAdmin(adminStatus);
              setIsAdminLoaded(true);
            }
          } catch (_) {
            if (!cancelled) {
              setIsAdmin(false);
              setIsAdminLoaded(true);
            }
          }
          // 그 다음 리다이렉트
          try { localStorage.removeItem('postLoginRedirect'); } catch (_) {}
          if (!cancelled) {
            navigate(redirect, { replace: true });
          }
        }
      }
    };
    handlePostLoginRedirect();
    return () => { cancelled = true; };
  }, [currentUser, navigate]);
  // 로그인 상태 변화 시 즉시 마케팅 동의 여부 및 관리자 권한 확인 (DB 조회)
  useEffect(() => {
    let cancelled = false;
    
    setCheckedMarketing(false);
    setShowMarketingModal(false);
    
    // 로그아웃 시에만 관리자 상태 초기화
    if (!currentUser) {
      setIsAdmin(false);
      setIsAdminLoaded(true);
      setCheckedMarketing(true);
      return;
    }
    
    // 로그인 중이면 로딩 상태로 설정
    setIsAdminLoaded(false);
    
    // 즉시 API 호출
    (async () => {
      try {
        const res = await getUserFromFirestore(currentUser.uid);
        if (!cancelled) {
          const data = res && res.data ? res.data : null;
          const agree = !!(data && data.agreeMarketing);
          const respondedAtStr = data && data.marketingRespondedAt;
          const hasResponded = !!respondedAtStr;
          const needs = !(agree || hasResponded);
          setShowMarketingModal(needs);
          setCheckedMarketing(true);
          
          // 관리자 권한 확인 - 즉시 업데이트
          const adminStatus = !!(data && data.isAdmin === true);
          setIsAdmin(adminStatus);
          setIsAdminLoaded(true);
        }
      } catch (_) {
        if (!cancelled) {
          setShowMarketingModal(false);
          setCheckedMarketing(true);
          setIsAdmin(false);
          setIsAdminLoaded(true);
        }
      }
    })();
    
    return () => { cancelled = true; };
  }, [currentUser]);

  // 알림 불러오기 및 주기적 갱신
  useEffect(() => {
    if (!currentUser) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    const loadNotifications = async () => {
      try {
        const result = await getUnreadNotifications(currentUser.uid);
        if (result.success) {
          setNotifications(result.data);
          setUnreadCount(result.count);
          
          // 읽지 않은 알림이 있으면 흔들림 효과
          if (result.count > 0 && !isNotificationOpen) {
            setShouldShake(true);
            setTimeout(() => setShouldShake(false), 1000);
          }
        }
      } catch (error) {
        console.error('알림 로드 오류:', error);
      }
    };

    // 즉시 로드
    loadNotifications();

    // 30초마다 갱신
    const interval = setInterval(loadNotifications, 30000);

    return () => clearInterval(interval);
  }, [currentUser, isNotificationOpen]);

  // 알림 읽음 처리 및 게시물로 이동
  const handleNotificationClick = async (notification) => {
    if (!currentUser || !notification) return;

    try {
      const result = await markNotificationAsRead(currentUser.uid, notification.id);
      if (result.success) {
        // 알림 목록에서 제거 (읽음 처리된 알림은 표시하지 않음)
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
        setUnreadCount(prev => Math.max(0, prev - 1));
        
        // 게시물로 이동
        if (notification.postUrl) {
          navigate(notification.postUrl);
          setIsNotificationOpen(false);
        }
      }
    } catch (error) {
      console.error('알림 읽음 처리 오류:', error);
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleProfileMenu = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen);
    setIsNotificationOpen(false); // 프로필 메뉴 열 때 알림 메뉴 닫기
  };

  // 프로필 메뉴를 열 때마다 관리자 권한 확인 (최신 상태 보장)
  useEffect(() => {
    let cancelled = false;
    const recheckAdmin = async () => {
      if (isProfileMenuOpen && currentUser) {
        try {
          const res = await getUserFromFirestore(currentUser.uid);
          if (!cancelled) {
            const data = res && res.data ? res.data : null;
            const adminStatus = !!(data && data.isAdmin === true);
            setIsAdmin(adminStatus);
            setIsAdminLoaded(true);
          }
        } catch (_) {
          if (!cancelled) {
            setIsAdmin(false);
            setIsAdminLoaded(true);
          }
        }
      }
    };
    recheckAdmin();
    return () => { cancelled = true; };
  }, [isProfileMenuOpen, currentUser]);

  const toggleNotificationMenu = () => {
    setIsNotificationOpen(!isNotificationOpen);
    setIsProfileMenuOpen(false); // 알림 메뉴 열 때 프로필 메뉴 닫기
  };

  const handleLogout = async () => {
    setIsProfileMenuOpen(false);
    try {
      await logout();
      // 로그아웃 후 화면 새로고침과 함께 홈페이지로 이동
      window.location.href = '/';
      console.log('로그아웃 완료, 홈페이지로 이동');
    } catch (error) {
      console.error('로그아웃 실패:', error);
    }
  };

  const handleProfileClick = () => {
    setIsProfileMenuOpen(false);
    navigate('/profile');
  };

  const handleAdminSettingsClick = () => {
    setIsProfileMenuOpen(false);
    navigate('/admin-settings');
  };

  // 네비게이션 핸들러들
  const handleHomeClick = () => {
    navigate('/');
    setCurrentPage('home');
  };

  const handleContentsClick = () => {
    navigate('/contents');
    setCurrentPage('contents');
  };

  const handlePricingClick = () => {
    navigate('/pricing');
    setCurrentPage('pricing');
  };

  const handleClassManagementClick = () => {
    if (!isLoggedIn) {
      setShowAuthRequiredModal(true);
      return;
    }
    navigate('/class-management');
    setCurrentPage('class-management');
  };

  const handleSupportCenterClick = () => {
    navigate('/help-center');
    setCurrentPage('help-center');
  };

  const handleSchoolRecordClick = () => {
    if (!isLoggedIn) {
      setShowAuthRequiredModal(true);
      return;
    }
    navigate('/school-record');
    setCurrentPage('school-record');
  };

  const handleLoginClick = () => {
    openLoginModal();
  };

  const handleSignupClick = () => {
    openRegisterModal();
  };

  // 클릭 외부 영역 감지
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      if (isProfileMenuOpen && !target.closest('.profile-menu-container')) {
        setIsProfileMenuOpen(false);
      }
      if (isNotificationOpen && !target.closest('.notification-container')) {
        setIsNotificationOpen(false);
      }
    };

    if (isProfileMenuOpen || isNotificationOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileMenuOpen, isNotificationOpen]);

  

  // 로그인/회원가입 페이지인지 확인
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register';

  return (
    <div style={{ height: 'var(--header-height)' }}>
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <button className="logo-link" onClick={() => { handleHomeClick(); setIsMobileMenuOpen(false); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <img src="/assets/images/teachering/title.svg" alt="티처링" className="logo-image" />
              </button>
            </div>
              <nav className="nav">
                <button
                  className={`nav-item ${currentPage === 'contents' ? 'active' : ''}`}
                  onClick={handleContentsClick}
                >
                  콘텐츠
                </button>

                <button
                  className={`nav-item ${currentPage === 'class-management' ? 'active' : ''} ${!isLoggedIn ? 'nav-item-locked' : ''}`}
                  onClick={handleClassManagementClick}
                >
                  학급관리
                  {!isLoggedIn && <Lock size={14} style={{ marginLeft: '6px', opacity: 0.7 }} />}
                </button>

                <button
                  className={`nav-item ${currentPage === 'school-record' ? 'active' : ''} ${!isLoggedIn ? 'nav-item-locked' : ''}`}
                  onClick={handleSchoolRecordClick}
                >
                  생기부
                  {!isLoggedIn && <Lock size={14} style={{ marginLeft: '6px', opacity: 0.7 }} />}
                </button>

                <button
                  className={`nav-item ${currentPage === 'pricing' ? 'active' : ''}`}
                  onClick={handlePricingClick}
                >
                  요금제
                </button>

                <button
                  className={`nav-item ${currentPage === 'help-center' ? 'active' : ''}`}
                  onClick={handleSupportCenterClick}
                >
                  지원센터
                </button>
              </nav>
            <div className="header-actions">
              {/* 데스크탑에서만 로그인/회원가입 버튼 표시 (로그인/회원가입 페이지에서는 숨김) */}
              {!isLoggedIn && !isAuthPage && (
                <>
                  <button className="btn-ghost" onClick={handleLoginClick}>
                    로그인
                  </button>
                  <button className="btn-simple" onClick={handleSignupClick}>
                    회원가입
                  </button>
                </>
              )}
              
              {/* 데스크탑에서만 학급관리 버튼과 프로필 메뉴 표시 (로그인/회원가입 페이지에서는 숨김) */}
              {isLoggedIn && userProfile && userProfile.name && !isAuthPage && (
                <>
                  {/* 알림 아이콘 */}
                  <div className="notification-container" style={{ position: 'relative' }}>
                    <button
                      onClick={toggleNotificationMenu}
                      className={shouldShake ? 'notification-shake' : ''}
                      style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '44px',
                        height: '44px',
                        border: 'none',
                        background: 'none',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <Bell size={24} color="#374151" />
                      {unreadCount > 0 && (
                        <span
                          style={{
                            position: 'absolute',
                            bottom: '2px',
                            right: '2px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '18px',
                            height: '18px',
                            padding: '0 4px',
                            borderRadius: '9px',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            fontSize: '11px',
                            fontWeight: '700',
                            border: '2px solid white',
                          }}
                        >
                          {unreadCount}
                        </span>
                      )}
                    </button>
                    <NotificationDropdown
                      isOpen={isNotificationOpen}
                      onClose={() => setIsNotificationOpen(false)}
                      notifications={notifications}
                      onNotificationClick={handleNotificationClick}
                    />
                  </div>

                  {/* PRO 뱃지 (데스크탑) */}
                  {isPro && (
                    <div style={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      gap: '4px',
                      padding: '4px 10px',
                      borderRadius: '16px',
                      background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                      fontSize: '12px',
                      fontWeight: '700',
                      color: '#fff',
                      boxShadow: '0 2px 6px rgba(255, 165, 0, 0.3)',
                    }}>
                      <Crown size={14} />
                      PRO
                    </div>
                  )}
                  <div className="profile-menu-container">
                    <button className="profile-menu-button desktop-only" onClick={toggleProfileMenu}>
                      <span className={`profile-menu-avatar ${userProfile.avatarUrl ? 'has-image' : ''}`}>
                        {userProfile.avatarUrl ? (
                          <img src={userProfile.avatarUrl} alt="프로필" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                        ) : (
                          <User size={16} />
                        )}
                      </span>
                      <span className="profile-menu-name">{userProfile.name}</span>
                    </button>
                    {isProfileMenuOpen && (
                      <div className="profile-menu-dropdown">
                        <div className="profile-menu-dropdown-header">
                          <div className="profile-menu-info">
                            <span className={`profile-menu-avatar-large ${userProfile.avatarUrl ? 'has-image' : ''}`}>
                              {userProfile.avatarUrl ? (
                                <img src={userProfile.avatarUrl} alt="프로필" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                              ) : (
                                <User size={20} />
                              )}
                            </span>
                            <div>
                              <div className="profile-menu-name-large">
                                {userProfile.name}
                                {isPro && (
                                  <span style={{ 
                                    marginLeft: '8px', 
                                    display: 'inline-flex', 
                                    alignItems: 'center', 
                                    gap: '4px',
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    color: '#fff',
                                    boxShadow: '0 2px 4px rgba(255, 165, 0, 0.3)'
                                  }}>
                                    <Crown size={12} />
                                    PRO
                                  </span>
                                )}
                              </div>
                              <div className="profile-menu-email">{userProfile.email}</div>
                            </div>
                          </div>
                        </div>
                        <div className="profile-menu-dropdown-menu">
                          <button className="profile-menu-item" onClick={handleProfileClick}>
                            <User size={16} /> 내 프로필
                          </button>
                          {isAdmin && (
                            <button className="profile-menu-item" onClick={handleAdminSettingsClick}>
                              <Settings size={16} /> 관리자 설정
                            </button>
                          )}
                          <div className="profile-menu-divider"></div>
                          <button className="profile-menu-item logout" onClick={handleLogout}>
                            <LogOut size={16} /> 로그아웃
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* 모바일에서만 햄버거 버튼 표시 */}
              <button 
                className={`mobile-menu-toggle ${isMobileMenuOpen ? 'open' : ''}`} 
                onClick={toggleMobileMenu}
              >
                <span></span>
                <span></span>
                <span></span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <LoginModal
        isOpen={showLoginModal}
        onClose={closeLoginModal}
        onSuccess={closeLoginModal}
        onRequestRegister={() => { closeLoginModal(); openRegisterModal(); }}
      />

      <RegisterModal
        isOpen={showRegisterModal}
        onClose={closeRegisterModal}
        onSuccess={closeRegisterModal}
        onRequestLogin={() => { closeRegisterModal(); openLoginModal(); }}
      />

      {/* <MarketingConsentModal
        isOpen={checkedMarketing && showMarketingModal}
        onClose={() => setShowMarketingModal(false)}
        onComplete={() => setShowMarketingModal(false)}
        userEmail={currentUser?.email || ''}
      /> */}

      {/* 회원가입 필요 모달 */}
      {showAuthRequiredModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
        }} onClick={() => setShowAuthRequiredModal(false)}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{
              textAlign: 'center',
              marginBottom: '24px',
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                backgroundColor: '#e6f7f4',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <User size={32} color="#16bf9f" />
              </div>
              <h2 style={{
                fontSize: '22px',
                fontWeight: '700',
                color: '#1a1a1a',
                marginBottom: '8px',
              }}>
                회원가입이 필요한 기능입니다
              </h2>
              <p style={{
                fontSize: '15px',
                color: '#666',
                lineHeight: '1.6',
              }}>
                무료 회원가입을 하시면<br />
                학급관리와 생기부 기능을 사용하실 수 있습니다
              </p>
            </div>
            <div style={{
              display: 'flex',
              gap: '12px',
              flexDirection: 'column',
            }}>
              <button
                onClick={() => {
                  setShowAuthRequiredModal(false);
                  openRegisterModal();
                }}
                style={{
                  width: '100%',
                  padding: '14px 24px',
                  backgroundColor: '#16bf9f',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#12a085'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#16bf9f'}
              >
                무료 회원가입하기
              </button>
              <button
                onClick={() => {
                  setShowAuthRequiredModal(false);
                  openLoginModal();
                }}
                style={{
                  width: '100%',
                  padding: '14px 24px',
                  backgroundColor: 'white',
                  color: '#16bf9f',
                  border: '2px solid #16bf9f',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#e6f7f4';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'white';
                }}
              >
                로그인하기
              </button>
              <button
                onClick={() => setShowAuthRequiredModal(false)}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  backgroundColor: 'transparent',
                  color: '#999',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'color 0.2s',
                }}
                onMouseEnter={(e) => e.target.style.color = '#666'}
                onMouseLeave={(e) => e.target.style.color = '#999'}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Sidebar */}
      <div className={`mobile-sidebar ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-sidebar-overlay" onClick={() => setIsMobileMenuOpen(false)}></div>
        <div className="mobile-sidebar-content">
          <div className="mobile-sidebar-header">
          </div>
          <nav className="mobile-nav">
            <button className="mobile-nav-item" onClick={() => { handleContentsClick(); setIsMobileMenuOpen(false); }}>
              콘텐츠
            </button>
            <button 
              className={`mobile-nav-item ${!isLoggedIn ? 'mobile-nav-item-locked' : ''}`} 
              onClick={() => { handleClassManagementClick(); setIsMobileMenuOpen(false); }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                학급관리
                {!isLoggedIn && <Lock size={16} style={{ opacity: 0.7 }} />}
              </span>
            </button>
            <button 
              className={`mobile-nav-item ${!isLoggedIn ? 'mobile-nav-item-locked' : ''}`} 
              onClick={() => { handleSchoolRecordClick(); setIsMobileMenuOpen(false); }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                생기부
                {!isLoggedIn && <Lock size={16} style={{ opacity: 0.7 }} />}
              </span>
            </button>
            <button className="mobile-nav-item" onClick={() => { handlePricingClick(); setIsMobileMenuOpen(false); }}>
              요금제
            </button>
            <button className="mobile-nav-item" onClick={() => { handleSupportCenterClick(); setIsMobileMenuOpen(false); }}>
              지원센터
            </button>
          </nav>
          {!isLoggedIn && !isAuthPage && (
            <div className="mobile-sidebar-actions">
              <button className="btn-primary mobile-login" onClick={() => { handleLoginClick(); setIsMobileMenuOpen(false); }}>
                로그인
              </button>
            </div>
          )}
          {isLoggedIn && userProfile && userProfile.name && !isAuthPage && (
            <div className="mobile-profile">
              <div className="mobile-profile-info">
                <span className={`profile-menu-avatar-large ${userProfile.avatarUrl ? 'has-image' : ''}`}>
                  {userProfile.avatarUrl ? (
                    <img src={userProfile.avatarUrl} alt="프로필" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                  ) : (
                    <User size={20} />
                  )}
                </span>
                <div style={{ flex: 1 }}>
                  <div className="profile-menu-name-large">
                    {userProfile.name}
                    {isPro && (
                      <span style={{ 
                        marginLeft: '8px', 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        gap: '4px',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                        fontSize: '11px',
                        fontWeight: '700',
                        color: '#fff',
                        boxShadow: '0 2px 4px rgba(255, 165, 0, 0.3)'
                      }}>
                        <Crown size={12} />
                        PRO
                      </span>
                    )}
                  </div>
                  <div className="profile-menu-email">{userProfile.email}</div>
                </div>
                {/* 모바일 알림 아이콘 */}
                <button
                  onClick={toggleNotificationMenu}
                  style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '44px',
                    height: '44px',
                    border: 'none',
                    background: 'none',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    flexShrink: 0,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <Bell size={24} color="#374151" />
                  {notifications.filter(n => !n.isRead).length > 0 && (
                    <span
                      style={{
                        position: 'absolute',
                        bottom: '2px',
                        right: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '18px',
                        height: '18px',
                        padding: '0 4px',
                        borderRadius: '9px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        fontSize: '11px',
                        fontWeight: '700',
                        border: '2px solid white',
                      }}
                    >
                      {notifications.filter(n => !n.isRead).length}
                    </span>
                  )}
                </button>
              </div>

              {/* 모바일 알림 목록 */}
              {isNotificationOpen && (
                <div style={{
                  margin: '16px 0',
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '12px',
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px',
                  }}>
                    <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#1a1a1a' }}>
                      알림
                    </h4>
                    {notifications.filter(n => !n.isRead).length > 0 && (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '20px',
                        height: '20px',
                        padding: '0 6px',
                        borderRadius: '10px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '700',
                      }}>
                        {notifications.filter(n => !n.isRead).length}
                      </span>
                    )}
                  </div>
                  {notifications.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '13px' }}>
                      새로운 알림이 없습니다
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {notifications.map((notification) => (
                        <div
                          key={notification.id}
                          style={{
                            padding: '12px',
                            backgroundColor: notification.isRead ? 'white' : '#e6f7f4',
                            borderRadius: '8px',
                            fontSize: '13px',
                          }}
                        >
                          <div style={{ fontWeight: notification.isRead ? '500' : '700', color: '#1a1a1a', marginBottom: '4px' }}>
                            {notification.title}
                          </div>
                          <div style={{ color: '#6b7280', fontSize: '12px', lineHeight: '1.5' }}>
                            {notification.message}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="mobile-profile-actions">
                <button className="mobile-profile-btn" onClick={() => { handleProfileClick(); setIsMobileMenuOpen(false); }}>
                  <User size={16} /> 내 프로필
                </button>
                {isAdmin && (
                  <button className="mobile-profile-btn" onClick={() => { handleAdminSettingsClick(); setIsMobileMenuOpen(false); }}>
                    <Settings size={16} /> 관리자 설정
                  </button>
                )}
                <button className="mobile-profile-btn logout" onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}>
                  <LogOut size={16} /> 로그아웃
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeacheringHeader; 